import React, { useState } from 'react';
import { Mail, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth'; // Adjust the import path as necessary

export const UserBadge: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { account, logout } = useAuth();

  if (!account) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full py-1.5 px-3 hover:bg-gray-50 transition-colors"
        title="User menu"
      >
        {/* Replace icon components with direct SVG */}
        <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        <svg className={`w-4 h-4 text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-sm text-gray-600">Signed in as</p>
            <p className="text-sm font-medium text-gray-900 truncate">{account.username}</p>
          </div>
          <div className="px-4 py-2 border-b border-gray-100">
            <Link
              to="/messages"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>View Message History</span>
            </Link>
            <button
              onClick={async () => {
                await logout();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Footer login component
const LoginSystem: React.FC = () => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const { loginWithProvider, loginWithEmail, loading, error, initialized, account } = useAuth();

  if (!initialized) {
    return <div>Initializing authentication...</div>;
  }

  // If user is logged in, don't show the login component at all
  if (account) {
    return null;
  }

  const handleProviderLogin = async (provider: 'google' | 'microsoft') => {
    try {
      await loginWithProvider(provider);
    } catch (error) {
      // Only log errors that aren't user cancellation or interaction_in_progress
      if (error instanceof Error &&
        !error.message.includes('user_cancelled') &&
        !error.message.includes('interaction_in_progress')) {
        console.error(`${provider} login failed:`, error);
      }
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await loginWithEmail({ email });
      setShowEmailForm(false);
      setEmail('');
    } catch (error) {
      console.error('Email login failed:', error);
    }
  };

  return (
    <div className="relative flex justify-center">
      {error && !error.includes('user_cancelled') && (
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-full max-w-sm bg-red-100 text-red-800 px-4 py-2 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      <div className="flex gap-4 items-center">
        <button
          onClick={() => handleProviderLogin('google')}
          disabled={loading}
          className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Sign in with Google"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z"
            />
          </svg>
        </button>

        <button
          onClick={() => handleProviderLogin('microsoft')}
          disabled={loading}
          className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Sign in with Microsoft"
        >
          <svg className="w-5 h-5" viewBox="0 0 23 23">
            <path fill="currentColor" d="M1 1h10v10H1z" />
            <path fill="currentColor" d="M1 12h10v10H1z" />
            <path fill="currentColor" d="M12 1h10v10H12z" />
            <path fill="currentColor" d="M12 12h10v10H12z" />
          </svg>
        </button>

        <button
          onClick={() => setShowEmailForm(true)}
          disabled={loading}
          className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Sign in with Email"
        >
          <Mail className="w-5 h-5 text-gray-900" />
        </button>
      </div>

      {showEmailForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-6 w-96 max-w-[calc(100vw-2rem)] relative">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Sign in with Email</h2>
              <button
                onClick={() => {
                  setShowEmailForm(false);
                  setEmail('');
                }}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="example@email.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Signing in..." : "Continue with Email"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginSystem;